import { useState, type ReactNode } from 'react';
import { Icon, Trash } from '@react-apps/design-system';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { QUESTION_IMAGE_NODE, QuestionImage } from '@react-apps/content-editor';
import { FIELD_TITLE_CLASS } from '../display';

/** O que o dono devolve depois de escolher e conferir um arquivo. */
export interface PickedImage {
  filename: string;
  /** Data URI para exibir dentro do editor. */
  src: string;
}

/**
 * Editor de texto rico (TipTap) para os campos de body. Emite HTML — que o back
 * sanitiza antes de persistir (fonte da verdade).
 *
 * **A barra é curta de propósito.** Tinha 11 botões (listas, títulos,
 * blockquote, código, link, limpar formatação) para um conteúdo que, medido no
 * importador, chega **sem formatação nenhuma**: o CSV trata `stem`/`alt_*_text`
 * como texto puro, e o que o material médico usa de fato é sobrescrito/subscrito
 * (mm³, cm², íons) e figura. Sobraram negrito, x², x₂ e imagem. O allowlist do
 * back continua aceitando o que já está gravado, então nada de conteúdo
 * existente se perde — só deixa de haver botão para produzir mais.
 *
 * `variant="compact"` é para alternativa: sem barra e sem altura mínima de
 * parágrafo, porque alternativa é quase sempre uma linha. Quatro alternativas
 * com a barra inteira eram quatro barras de 11 botões e ~130px cada.
 */
export function RichTextEditor({
  label,
  value,
  onChange,
  variant = 'default',
  onPickImage,
  autoFocus = false,
}: {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  variant?: 'default' | 'compact';
  /**
   * Escolhe/confere uma imagem e devolve como exibi-la, ou `null` quando foi
   * recusada/cancelada — e aí nada é inserido, em vez de uma figura apontando
   * para arquivo que não subiu.
   */
  onPickImage?: () => Promise<PickedImage | null>;
  /** Foco no fim do texto ao montar — para quem entra em edição por um botão. */
  autoFocus?: boolean;
}) {
  const compact = variant === 'compact';
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: {
            rel: 'noopener noreferrer nofollow',
            target: '_blank',
          },
        },
      }),
      Subscript,
      Superscript,
      QuestionImage,
    ],
    content: value,
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: [
          'rich-text w-full px-3 py-2 focus:outline-none',
          // `pr-16` abre espaço para os ícones ancorados à direita — sem isso
          // o texto da alternativa corre por baixo deles.
          compact ? 'min-h-9 pr-16' : 'min-h-24 rounded-b-lg',
        ].join(' '),
      },
    },
  });

  const body = (
    <div className="border-(--color-border) bg-(--color-bg-secondary) relative rounded-lg border">
      {editor && !compact && (
        <Toolbar editor={editor} onPickImage={onPickImage} />
      )}
      <EditorContent editor={editor} />
      {editor && compact && onPickImage && (
        /* Na alternativa a ação de imagem mora na própria linha, ancorada à
           direita — é o que dispensa a barra sem esconder a ação. */
        <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
          <ImageActions editor={editor} onPickImage={onPickImage} />
        </div>
      )}
    </div>
  );

  if (!label) return body;
  return (
    <div className="space-y-1.5">
      <span className={FIELD_TITLE_CLASS}>{label}</span>
      {body}
    </div>
  );
}

// --- Toolbar -----------------------------------------------------------------

function Toolbar({
  editor,
  onPickImage,
}: {
  editor: Editor;
  onPickImage?: () => Promise<PickedImage | null>;
}) {
  return (
    <div className="border-(--color-border) flex flex-wrap items-center gap-1 border-b p-1.5">
      <ToolbarButton
        label="B"
        title="Negrito"
        bold
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        label="x²"
        title="Sobrescrito (mm³, cm²)"
        active={editor.isActive('superscript')}
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
      />
      <ToolbarButton
        label="x₂"
        title="Subscrito (H₂O)"
        active={editor.isActive('subscript')}
        onClick={() => editor.chain().focus().toggleSubscript().run()}
      />
      {onPickImage && (
        <>
          <span className="bg-(--color-border) mx-1 h-5 w-px" aria-hidden />
          <ImageActions editor={editor} onPickImage={onPickImage} withLabels />
        </>
      )}
      {/* Apagar a figura sem querer é o acidente mais fácil de cometer num
          `atom` node — um Backspace no lugar errado. Ctrl+Z já resolvia (o
          histórico vem no StarterKit), mas ninguém adivinha que o editor tem
          histórico: o botão é o que torna a saída visível. Desfazer é melhor
          que "restaurar tudo" porque devolve **só** a imagem, sem jogar fora o
          resto do que foi digitado. */}
      <span className="ml-auto flex items-center gap-1">
        <ToolbarButton
          label={<UndoIcon />}
          title="Desfazer (Ctrl+Z) — traz de volta a imagem ou o texto apagado"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        />
        <ToolbarButton
          label={<RedoIcon />}
          title="Refazer (Ctrl+Shift+Z)"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        />
      </span>
    </div>
  );
}

/**
 * Ícone de imagem. O conjunto do design-system não tem um, e a primitiva `Icon`
 * é exportada justamente para isto — desenhar aqui mantém traço, escala e cor de
 * tema iguais aos outros ícones, sem mexer no pacote compartilhado por um caso
 * de uso só deste editor.
 */
function ImageIcon() {
  return (
    <Icon size="sm">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-5-5L5 21" />
    </Icon>
  );
}

function UndoIcon() {
  return (
    <Icon size="sm">
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 0 10h-4" />
    </Icon>
  );
}

function RedoIcon() {
  return (
    <Icon size="sm">
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H9a5 5 0 0 0 0 10h4" />
    </Icon>
  );
}

/**
 * Inserir / trocar / remover imagem. Os botões **mudam com a seleção**: um nó de
 * imagem selecionado é o único momento em que "trocar" e "remover" fazem
 * sentido, e é a alternativa a um bubble menu (que exigiria dependência nova) ou
 * a esperar que o autor descubra sozinho que Delete apaga a figura.
 */
function ImageActions({
  editor,
  onPickImage,
  withLabels,
}: {
  editor: Editor;
  onPickImage: () => Promise<PickedImage | null>;
  withLabels?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const selected = editor.isActive(QUESTION_IMAGE_NODE);

  const pick = async (replace: boolean) => {
    setBusy(true);
    try {
      const picked = await onPickImage();
      if (!picked) return;
      // Escolher + conferir os bytes leva tempo real (um arquivo de 9 MB é lido
      // e farejado antes de voltar), e nesse meio-tempo o editor pode ter
      // deixado de existir: trocar para a aba Preview desmonta os editores, e a
      // guarda de edição fecha o card. Mexer nele depois disso estoura
      // `Cannot read properties of null (reading 'chain')` — uma rejection não
      // tratada, sem nada na tela dizendo o que aconteceu.
      if (editor.isDestroyed) return;
      const attrs = {
        filename: picked.filename,
        src: picked.src,
        alt: picked.filename,
        // Trocar zera o `fileId`: os bytes agora são locais, e é o `filename`
        // que o back vai casar com o `images[]` do payload.
        fileId: null,
      };
      if (replace) {
        editor
          .chain()
          .focus()
          .updateAttributes(QUESTION_IMAGE_NODE, attrs)
          .run();
        return;
      }
      editor
        .chain()
        .focus()
        .insertContent({ type: QUESTION_IMAGE_NODE, attrs })
        .run();
    } finally {
      setBusy(false);
    }
  };

  if (selected) {
    return (
      <>
        <ToolbarButton
          label={withLabels ? busy ? 'Trocando…' : 'Trocar' : <ImageIcon />}
          title="Trocar esta imagem por outra (o texto não muda)"
          onClick={() => void pick(true)}
        />
        <ToolbarButton
          label={withLabels ? 'Remover' : <Trash size="sm" />}
          title="Remover esta imagem da questão"
          onClick={() => editor.chain().focus().deleteSelection().run()}
        />
      </>
    );
  }

  return (
    <ToolbarButton
      label={withLabels ? busy ? 'Imagem…' : 'Imagem' : <ImageIcon />}
      title="Inserir uma imagem na posição do cursor"
      onClick={() => void pick(false)}
    />
  );
}

function ToolbarButton({
  label,
  title,
  onClick,
  active,
  bold,
  disabled,
}: {
  label: ReactNode;
  title: string;
  onClick: () => void;
  active?: boolean;
  bold?: boolean;
  disabled?: boolean;
}) {
  const style = [
    'inline-flex items-center rounded px-2 py-1 text-sm',
    'disabled:pointer-events-none disabled:opacity-40',
    bold ? 'font-bold' : '',
    active
      ? 'bg-(--color-accent)/15 text-(--color-accent)'
      : 'text-(--color-text-secondary) hover:bg-(--color-bg-primary)',
  ].join(' ');

  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={style}
    >
      {label}
    </button>
  );
}
